import type {
  LineageColorBy,
  LineageColorCategory,
  LineageDisplaySettings,
  LineageNodeDetailField,
  LineageNodeMetadataField,
} from './types';

export const GRAPH_NODE_MIN_WIDTH = 200;

export const GRAPH_NODE_MAX_WIDTH = 450;

export const GRAPH_NODE_HEIGHT = 74;

export const RESTRICTED_NODE_HEIGHT = 52;

export const GRAPH_NODE_CHILDREN_HEADER_HEIGHT = 38;

export const GRAPH_PARENT_NODE_HEIGHT =
  GRAPH_NODE_HEIGHT + GRAPH_NODE_CHILDREN_HEADER_HEIGHT;

export const GRAPH_CHILD_NODE_HEIGHT = 40;

export const GRAPH_NODE_VERTICAL_SPACING = 50;

export const GRAPH_NODE_HORIZONTAL_SPACING = 130;

export const GRAPH_CHILD_NODE_VERTICAL_SPACING = 20;

export const GRAPH_NODE_SEARCH_AREA_HEIGHT = 60;

export const CHILD_NODE_PADDING = 8;

export const NODE_Z_INDEX = 2;
export const EXPANDED_NODE_Z_INDEX = 3;

export const MAX_GRAPH_NODES_WARNING = 1350;
export const MAX_GRAPH_EDGES_WARNING = 3900;
export const LINEAGE_PER_PARENT_COLUMNS_LIMIT = 300;

export const LINEAGE_PILL_ZOOM_THRESHOLD = 0.4;
export const LINEAGE_DOT_ZOOM_THRESHOLD = 0.2;
export const LINEAGE_DOT_MIN_DIAMETER = 17;
export const LINEAGE_DOT_MAX_DIAMETER = 90;
export const LINEAGE_PILL_MIN_WIDTH = 220;
export const LINEAGE_PILL_HEIGHT = 56;

// The pill is centered on the card's edge-anchor offset; in pill form an
// expanded node's rows stack from its bottom edge.
export const LINEAGE_PILL_BOTTOM =
  (GRAPH_NODE_HEIGHT + LINEAGE_PILL_HEIGHT) / 2;

export const DEFAULT_LINEAGE_DISPLAY_SETTINGS: LineageDisplaySettings = {
  colorBy: 'assetType',
  nodeMetadata: {
    testResults: true,
    criticalAsset: true,
  },
  detailField: 'assetType',
};

export const LINEAGE_NODE_METADATA_OPTIONS: {
  value: LineageNodeMetadataField;
  label: string;
  requiresTests?: boolean;
}[] = [
  { value: 'testResults', label: 'Test results', requiresTests: true },
  { value: 'criticalAsset', label: 'Critical asset' },
];

export const LINEAGE_NODE_DETAIL_LABELS: Record<
  LineageNodeDetailField,
  string
> = {
  none: 'None',
  assetType: 'Asset type',
  location: 'Database & schema',
  database: 'Database',
  schema: 'Schema',
  materialization: 'Materialization',
  owners: 'Owners',
  tags: 'Tags',
  path: 'Path',
};

const LINEAGE_NODE_DETAIL_ORDER: LineageNodeDetailField[] = [
  'none',
  'assetType',
  'location',
  'database',
  'schema',
  'materialization',
  'owners',
  'tags',
  'path',
];

export const LINEAGE_NODE_DETAIL_OPTIONS = LINEAGE_NODE_DETAIL_ORDER.map(
  (value) => ({ value, label: LINEAGE_NODE_DETAIL_LABELS[value] }),
);

export const LINEAGE_COLOR_BY_OPTIONS: {
  value: LineageColorBy;
  label: string;
  requiresTests?: boolean;
}[] = [
  { value: 'assetType', label: 'Asset type' },
  { value: 'testStatus', label: 'Test status', requiresTests: true },
  { value: 'none', label: 'None' },
];

export const LINEAGE_COLOR_BY_LEGEND: Record<
  LineageColorBy,
  { category: LineageColorCategory; label: string }[]
> = {
  assetType: [
    { category: 'source', label: 'Source' },
    { category: 'dataAsset', label: 'Data asset' },
    { category: 'exposure', label: 'Exposure' },
    { category: 'semantic', label: 'Semantic' },
  ],
  testStatus: [
    { category: 'failing', label: 'Failure' },
    { category: 'warning', label: 'Warning' },
    { category: 'passing', label: 'Passed' },
    { category: 'neutral', label: 'No test results' },
  ],
  none: [],
};

// Ring (and column row tint) for nodes matching the lineage filter, drawn the
// same under every Color by option. Sizes are in px at card zoom.
export const LINEAGE_FILTER_HIGHLIGHT = { opacity: 0.1, width: 6 };

export const LINEAGE_SELECTED_SHADOW = { opacity: 0.2, blur: 16, offsetY: 4 };

// Pills and dots render zoomed out, so their filter ring and selected shadow
// are scaled up to read the same as on cards.
export const LINEAGE_COMPACT_HIGHLIGHT_SCALE = 2;

// Dark blue ring around dots on the selected path, which keep their Color by fill.
export const LINEAGE_DOT_PATH_RING_WIDTH = 10;

export const COLLECT_JOB_DATA_DOCS_URL =
  'https://docs.elementary-data.com/deployment-and-configuration/collect-job-data';
